import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Container,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
  TextField,
  InputAdornment,
  Button,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Snackbar,
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  Search as SearchIcon,
  MyLocation as MyLocationIcon,
  Restaurant as RestaurantIcon,
  AccessTime as TimeIcon,
  Phone as PhoneIcon,
  Directions as DirectionsIcon,
  ShoppingCart as TakeawayIcon,
  List as ListIcon,
  Wifi as WifiIcon,
  LocalParking as ParkingIcon,
  OutdoorGrill as TerrasIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import TakeawayView from './TakeawayView';
import { apiService, Location } from '../services/api';

// Fix for default markers in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Language translations
const translations = {
  nl: {
    reserveNow: 'Reserveer nu',
    takeaway: 'Takeaway',
    findMyLocation: 'Vind mijn locatie',
    searchPlaceholder: 'Zoek op naam, adres of postcode',
    city: 'Stad',
    allCities: 'Alle steden',
    openUntil: 'Open tot',
    closed: 'Gesloten',
    reserveHere: 'Reserveer hier',
    route: 'Route',
    close: 'Sluiten',
    today: 'Vandaag',
    persons: 'personen',
    loading: 'Laden...',
    noResults: 'Geen resultaten gevonden',
    error: 'Er is een fout opgetreden',
    success: 'Succesvol',
    availableTables: 'beschikbare tafels',
    reviews: 'reviews',
    rating: 'beoordeling',
  },
  fr: {
    reserveNow: 'Réserver maintenant',
    takeaway: 'À emporter',
    findMyLocation: 'Trouver ma position',
    searchPlaceholder: 'Rechercher par nom, adresse ou code postal',
    city: 'Ville',
    allCities: 'Toutes les villes',
    openUntil: 'Ouvert jusqu\'à',
    closed: 'Fermé',
    reserveHere: 'Réserver ici',
    route: 'Itinéraire',
    close: 'Fermer',
    today: 'Aujourd\'hui',
    persons: 'personnes',
    loading: 'Chargement...',
    noResults: 'Aucun résultat trouvé',
    error: 'Une erreur s\'est produite',
    success: 'Succès',
    availableTables: 'tables disponibles',
    reviews: 'avis',
    rating: 'évaluation',
  },
  en: {
    reserveNow: 'Book now',
    takeaway: 'Takeaway',
    findMyLocation: 'Find my location',
    searchPlaceholder: 'Search by name, address or postal code',
    city: 'City',
    allCities: 'All cities',
    openUntil: 'Open until',
    closed: 'Closed',
    reserveHere: 'Book here',
    route: 'Route',
    close: 'Close',
    today: 'Today',
    persons: 'persons',
    loading: 'Loading...',
    noResults: 'No results found',
    error: 'An error occurred',
    success: 'Success',
    availableTables: 'available tables',
    reviews: 'reviews',
    rating: 'rating',
  }
};

const GuestReservationApp: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [showTakeaway, setShowTakeaway] = useState(false);
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [clickedLocation, setClickedLocation] = useState<Location | null>(null);
  const [reservationDialogOpen, setReservationDialogOpen] = useState(false);
  const [reservationLocation, setReservationLocation] = useState<Location | null>(null);
  const [timeSlotDialogOpen, setTimeSlotDialogOpen] = useState(false);
  const [mapZoom, setMapZoom] = useState(7);
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [language, setLanguage] = useState<'nl' | 'fr' | 'en'>('nl');
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState<{open: boolean, message: string, severity: 'success' | 'error' | 'info'}>({
    open: false,
    message: '',
    severity: 'info'
  });
  const [reservationForm, setReservationForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    date: '',
    time: '18:00',
    guests: 2,
    notes: ''
  });
  const [showMapOnMobile, setShowMapOnMobile] = useState(false);
  const [mapCenter, setMapCenter] = useState<[number, number]>([50.8503, 4.3517]); // Belgium center
  const mapRef = useRef<any>(null);
  const [locations, setLocations] = useState<Location[]>([]);

  const t = translations[language];

  // Major cities in Belgium for dropdown
  const majorCities = [
    t.allCities,
    'Antwerpen',
    'Brussel',
    'Gent',
    'Brugge',
    'Leuven',
    'Mechelen',
    'Aalst',
    'Kortrijk',
    'Hasselt',
    'Genk',
    'Sint-Niklaas',
    'Oostende',
    'Roeselare',
    'Turnhout',
    'Lokeren',
    'Beveren',
    'Dendermonde',
    'Geel',
    'Herentals'
  ];

  // Quick reservation options
  const quickReservationOptions = [
    { time: '19:00', guests: 2, label: `${t.today} 19:00 - 2 ${t.persons}` },
    { time: '20:00', guests: 4, label: `${t.today} 20:00 - 4 ${t.persons}` },
    { time: '21:00', guests: 6, label: `${t.today} 21:00 - 6 ${t.persons}` },
  ];

  // Effect to handle map movement when center or zoom changes
  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.setView(mapCenter, mapZoom);
    }
  }, [mapCenter, mapZoom]);

  // Load locations from API
  useEffect(() => {
    const loadLocations = async () => {
      try {
        const data = await apiService.getLocations();
        setLocations(data);
      } catch (error) {
        console.error('Error loading locations:', error);
      } finally {
        setLoading(false);
      }
    };

    loadLocations();
  }, []);

  const filteredLocations = locations.filter(location => {
    const matchesSearch = location.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      location.city.toLowerCase().includes(searchQuery.toLowerCase()) ||
      location.address.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesCity = selectedCity === '' || selectedCity === t.allCities || 
      location.city.toLowerCase().includes(selectedCity.toLowerCase());
    
    return matchesSearch && matchesCity;
  });

  const handleLocationClick = (location: Location) => {
    setClickedLocation(location);
    setLocationDialogOpen(true);
    // Zoom map to clicked location
    setMapCenter(location.coordinates);
    setMapZoom(15);
  };

  const handleReservation = (location: Location) => {
    setReservationLocation(location);
    setTimeSlotDialogOpen(true);
    setLocationDialogOpen(false);
  };

  const handleTakeaway = (location: Location) => {
    setSelectedLocation(location);
    setShowTakeaway(true);
    setLocationDialogOpen(false);
  };

  const handleMyLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLat = position.coords.latitude;
          const userLng = position.coords.longitude;
          
          console.log('Current location:', { lat: userLat, lng: userLng });
          
          // Center map on user location
          setMapCenter([userLat, userLng]);
          setMapZoom(12);
          
          // Find nearest Poule&Poulette location
          let nearestLocation: Location | null = null;
          let shortestDistance = Infinity;
          
          locations.forEach(location => {
            const distance = calculateDistance(
              userLat, userLng,
              location.coordinates[0], location.coordinates[1]
            );
            
            if (distance < shortestDistance) {
              shortestDistance = distance;
              nearestLocation = location;
            }
          });
          
          if (nearestLocation) {
            const distanceKm = (shortestDistance / 1000).toFixed(1);
            // Show the nearest location in the dialog
            setClickedLocation(nearestLocation as Location);
            setLocationDialogOpen(true);
            // Add distance info to the location temporarily
            (nearestLocation as any).distance = distanceKm;
          }
        },
        (error) => {
          console.error('Error getting location:', error);
          alert('Kon je locatie niet bepalen. Controleer of je locatie toegang hebt gegeven.');
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000
        }
      );
    } else {
      alert('Geolocatie wordt niet ondersteund door je browser.');
    }
  };

  // Helper function to calculate distance between two coordinates (Haversine formula)
  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c; // Distance in meters
  };

  const generateTimeSlots = (date: string) => {
    const slots = [];
    const startHour = 12; // Start om 12:00
    const endHour = 22; // Eind om 22:00
    const slotDuration = 75; // 1u15 in minuten
    
    for (let hour = startHour; hour < endHour; hour++) {
      const time = `${hour.toString().padStart(2, '0')}:00`;
      const endTime = `${(hour + 1).toString().padStart(2, '0')}:15`;
      
      // Simuleer bezette slots (voor demo doeleinden)
      const isOccupied = Math.random() < 0.3; // 30% kans dat een slot bezet is
      
      slots.push({
        time,
        endTime,
        isOccupied,
        display: `${time} - ${endTime}`
      });
    }
    
    return slots;
  };

  const handleTimeSlotSelect = (time: string) => {
    setReservationForm(prev => ({ ...prev, time, date: selectedDate }));
    setTimeSlotDialogOpen(false);
    setReservationDialogOpen(true);
  };

  const handleSubmitReservation = async () => {
    if (!reservationLocation) return;
    
    try {
      const reservationData = {
        locationId: reservationLocation.id,
        locationName: reservationLocation.name,
        customerName: `${reservationForm.firstName} ${reservationForm.lastName}`,
        customerEmail: reservationForm.email,
        customerPhone: reservationForm.phone,
        date: reservationForm.date,
        time: reservationForm.time,
        guests: reservationForm.guests,
        specialRequests: reservationForm.notes
      };
      
      const result = await apiService.submitReservation(reservationData);
      
      if (result.success) {
        // Reset form and close dialog
        setReservationForm({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          date: '',
          time: '18:00',
          guests: 2,
          notes: ''
        });
        setReservationDialogOpen(false);
        setReservationLocation(null);
        
        alert('Reservering succesvol ingediend!');
      } else {
        alert(`Fout: ${result.error}`);
      }
    } catch (error) {
      console.error('Error submitting reservation:', error);
      alert('Er is een fout opgetreden bij het indienen van de reservering.');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {showTakeaway ? (
        <TakeawayView 
          onBack={() => setShowTakeaway(false)} 
          location={selectedLocation ? {
            name: selectedLocation.name,
            address: selectedLocation.address,
            city: selectedLocation.city
          } : undefined}
        />
      ) : (
        <>
          {/* Header */}
          <AppBar position="static" elevation={0}>
            <Toolbar sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <RestaurantIcon sx={{ fontSize: 32, color: '#000000' }} />
                <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#000000' }}>
                  Poule&Poulette
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                {/* Language Switcher */}
                <ToggleButtonGroup
                  value={language}
                  exclusive
                  onChange={(e, newLanguage) => newLanguage && setLanguage(newLanguage)}
                  size="small"
                  sx={{ 
                    backgroundColor: '#f5f5f5',
                    '& .MuiToggleButton-root': {
                      border: 'none',
                      px: 2,
                      py: 0.5,
                      fontSize: '0.8rem',
                      fontWeight: 'bold',
                      '&.Mui-selected': {
                        backgroundColor: '#000000',
                        color: '#ffffff',
                        '&:hover': { backgroundColor: '#333333' }
                      }
                    }
                  }}
                >
                  <ToggleButton value="nl" aria-label="Nederlands">NL</ToggleButton>
                  <ToggleButton value="fr" aria-label="Français">FR</ToggleButton>
                  <ToggleButton value="en" aria-label="English">EN</ToggleButton>
                </ToggleButtonGroup>
                
                {/* Sticky Reserveer Nu Button */}
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => {
                    if (locations.length > 0) {
                      handleLocationClick(locations[0]);
                    }
                  }}
                  sx={{ 
                    backgroundColor: '#4caf50',
                    color: '#ffffff',
                    fontWeight: 'bold',
                    px: 3,
                    py: 1.5,
                    fontSize: '1rem',
                    minWidth: 140,
                    height: 48,
                    '&:hover': { backgroundColor: '#45a049' }
                  }}
                >
                  {t.reserveNow}
                </Button>
                
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<TakeawayIcon />}
                  onClick={() => setShowTakeaway(true)}
                  sx={{ 
                    backgroundColor: '#000000',
                    color: '#ffffff',
                    fontWeight: 'bold',
                    px: 3,
                    py: 1.5,
                    fontSize: '1rem',
                    minWidth: 140,
                    height: 48,
                    '&:hover': { backgroundColor: '#333333' }
                  }}
                >
                  {t.takeaway}
                </Button>
              </Box>
            </Toolbar>
          </AppBar>

          {/* Search Bar */}
          <Box sx={{ p: 4, backgroundColor: '#f8f9fa', borderBottom: '1px solid #e0e0e0' }}>
            <Container maxWidth="lg">
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexDirection: { xs: 'column', md: 'row' } }}>
                <Box sx={{ flex: 1, width: '100%' }}>
                  <TextField
                    fullWidth
                    placeholder={t.searchPlaceholder}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                    aria-label={t.searchPlaceholder}
                  />
                </Box>
                <FormControl sx={{ minWidth: { xs: '100%', md: 200 } }}>
                  <InputLabel>{t.city}</InputLabel>
                  <Select
                    value={selectedCity}
                    label={t.city}
                    onChange={(e) => setSelectedCity(e.target.value)}
                    sx={{ height: 56 }}
                  >
                    {majorCities.map((city) => (
                      <MenuItem key={city} value={city}>
                        {city}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box sx={{ minWidth: { xs: '100%', md: 200 } }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<MyLocationIcon />}
                    onClick={handleMyLocation}
                    sx={{ height: 56 }}
                    aria-label={t.findMyLocation}
                  >
                    {t.findMyLocation}
                  </Button>
                </Box>
              </Box>
            </Container>
          </Box>

          {/* Main Content */}
          <Box sx={{ flexGrow: 1, display: 'flex', height: 'calc(100vh - 280px)', mb: 2 }}>
            {/* Location List - Hidden on mobile when map is shown */}
            {(!isMobile || !showMapOnMobile) && (
              <Box sx={{ 
                width: isMobile ? '100%' : 400, 
                borderRight: isMobile ? 'none' : '1px solid #e0e0e0',
                backgroundColor: '#ffffff',
                display: 'flex',
                flexDirection: 'column'
              }}>
                {/* Scrollable List Container */}
                <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                  <List sx={{ p: 0 }}>
                    {filteredLocations.map((location) => (
                      <ListItem key={location.id} disablePadding>
                        <ListItemButton
                          onClick={() => handleLocationClick(location)}
                                                  sx={{
                          borderBottom: '1px solid #f0f0f0',
                          '&:hover': { backgroundColor: '#f8f9fa' },
                          p: 3
                        }}
                        >
                          <ListItemIcon>
                            <LocationIcon sx={{ color: '#000000' }} />
                          </ListItemIcon>
                          <ListItemText
                                                      primary={
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                              {location.name}
                            </Typography>
                          }
                            secondary={
                              <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                  {location.address}, {location.postcode} {location.city}
                                </Typography>
                                                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              mb: 1,
                              backgroundColor: location.isOpen ? '#e8f5e8' : '#ffeaea',
                              borderRadius: 1,
                              px: 1,
                              py: 0.5,
                              border: `1px solid ${location.isOpen ? '#4caf50' : '#f44336'}`
                            }}>
                              <TimeIcon sx={{ 
                                fontSize: 16, 
                                mr: 0.5, 
                                color: location.isOpen ? '#2e7d32' : '#d32f2f'
                              }} />
                              <Typography variant="body2" sx={{ 
                                fontWeight: 'bold',
                                color: location.isOpen ? '#2e7d32' : '#d32f2f'
                                                             }}>
                                 {location.isOpen ? `${t.openUntil} ${location.closingTime}` : t.closed}
                              </Typography>
                            </Box>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                  <PhoneIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                                  {location.phone}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                                  {location.services.slice(0, 3).map((service) => (
                                    <Chip
                                      key={service}
                                      label={service}
                                      size="small"
                                      variant="outlined"
                                      sx={{ fontSize: '0.6rem', height: 20 }}
                                    />
                                  ))}
                                </Box>
                              </Box>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </Box>
            )}

            {/* Map - Always visible on desktop, conditional on mobile */}
            {(!isMobile || showMapOnMobile) && (
              <Box sx={{ flexGrow: 1, height: '100%' }}>
                <MapContainer
                  ref={mapRef}
                  center={mapCenter}
                  zoom={mapZoom}
                  style={{ height: '100%', width: '100%' }}
                >
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  />
                  {locations.map((location) => (
                    <Marker
                      key={location.id}
                      position={location.coordinates}
                      eventHandlers={{
                        click: () => handleLocationClick(location),
                      }}
                    />
                  ))}
                </MapContainer>
              </Box>
            )}
          </Box>

          {/* Mobile Map Toggle */}
          {isMobile && (
            <Box sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={showMapOnMobile ? <ListIcon /> : <LocationIcon />}
                onClick={() => setShowMapOnMobile(!showMapOnMobile)}
              >
                {showMapOnMobile ? 'Toon lijst' : 'Toon kaart'}
              </Button>
            </Box>
          )}

          {/* Sticky Mobile CTA */}
          {isMobile && (
            <Box sx={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: '#ffffff',
              borderTop: '2px solid #4caf50',
              p: 2,
              zIndex: 1000,
              boxShadow: '0 -2px 10px rgba(0,0,0,0.1)'
            }}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={() => {
                  if (locations.length > 0) {
                    handleLocationClick(locations[0]);
                  }
                }}
                sx={{
                  backgroundColor: '#4caf50',
                  color: '#ffffff',
                  fontWeight: 'bold',
                  py: 1.5,
                  fontSize: '1.1rem',
                  '&:hover': { backgroundColor: '#45a049' }
                }}
              >
                {t.reserveNow} - {selectedLocation?.name || 'Poule&Poulette'}
              </Button>
            </Box>
          )}

          {/* CTA Banner */}
          <Box sx={{ 
            backgroundColor: '#f8f9fa',
            borderTop: '1px solid #e0e0e0',
            borderBottom: '1px solid #e0e0e0',
            py: 2,
            px: 2
          }}>
            <Container maxWidth="lg">
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, 
                gap: 2,
                alignItems: 'center'
              }}>
                {/* Content */}
                <Box sx={{ textAlign: { xs: 'center', md: 'left' } }}>
                  <Typography variant="h6" sx={{ 
                    fontWeight: 'bold', 
                    mb: 0.5,
                    fontSize: { xs: '1.25rem', md: '1.5rem' },
                    color: '#000000'
                  }}>
                    Proef Poule&Poulette
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    mb: 1.5, 
                    color: '#666',
                    lineHeight: 1.4,
                    fontSize: { xs: '0.85rem', md: '0.9rem' }
                  }}>
                    Traditionele kipgerechten en Belgische specialiteiten
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 1, 
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: { xs: 'center', md: 'flex-start' },
                    mb: 2
                  }}>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => setShowTakeaway(true)}
                      sx={{ 
                        backgroundColor: '#000000',
                        color: '#ffffff',
                        py: 0.75,
                        px: 2,
                        fontSize: '0.8rem',
                        fontWeight: 'bold',
                        '&:hover': { backgroundColor: '#333333' }
                      }}
                    >
                      {t.takeaway}
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => {
                        if (locations.length > 0) {
                          handleLocationClick(locations[0]);
                        }
                      }}
                      sx={{ 
                        backgroundColor: '#000000',
                        color: '#ffffff',
                        py: 0.75,
                        px: 2,
                        fontSize: '0.8rem',
                        fontWeight: 'bold',
                        '&:hover': { 
                          backgroundColor: '#333333'
                        }
                      }}
                    >
                      Reserveren
                    </Button>
                  </Box>
                  
                  {/* Quick Reservation Buttons */}
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 1, 
                    flexDirection: { xs: 'column', sm: 'row' },
                    justifyContent: { xs: 'center', md: 'flex-start' },
                    flexWrap: 'wrap'
                  }}>
                    {quickReservationOptions.map((option, index) => (
                      <Button
                        key={index}
                        variant="contained"
                        size="small"
                        onClick={() => {
                          if (locations.length > 0) {
                            setReservationForm(prev => ({ 
                              ...prev, 
                              time: option.time, 
                              guests: option.guests 
                            }));
                            handleLocationClick(locations[0]);
                          }
                        }}
                        sx={{ 
                          backgroundColor: '#4caf50',
                          color: '#ffffff',
                          py: 0.5,
                          px: 1.5,
                          fontSize: '0.75rem',
                          fontWeight: 'bold',
                          '&:hover': { backgroundColor: '#45a049' }
                        }}
                      >
                        {option.label}
                      </Button>
                    ))}
                  </Box>
                </Box>

                {/* Image */}
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: { xs: 'center', md: 'flex-end' },
                  alignItems: 'center'
                }}>
                  <Box sx={{
                    width: { xs: '100%', md: '180px' },
                    height: { xs: '100px', md: '120px' },
                    borderRadius: 1,
                    overflow: 'hidden',
                    position: 'relative',
                    background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
                    border: '1px solid #e0e0e0'
                  }}>
                    <Box sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'url("https://images.unsplash.com/photo-1555939594-58d7cb561ad1?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80")',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      opacity: 0.7
                    }} />
                    <Box sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: 'linear-gradient(45deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.05) 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Typography variant="body1" sx={{ 
                        color: '#ffffff', 
                        fontWeight: 'bold',
                        textAlign: 'center',
                        textShadow: '1px 1px 2px rgba(0,0,0,0.5)',
                        fontSize: { xs: '0.8rem', md: '1rem' }
                      }}>
                        Poule&Poulette
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Container>
          </Box>

          {/* Footer */}
          <Box sx={{ 
            backgroundColor: '#f8f9fa', 
            borderTop: '1px solid #e0e0e0',
            p: 4,
            mt: 'auto'
          }}>
            <Container maxWidth="lg">
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 4
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <RestaurantIcon sx={{ fontSize: 20, color: '#000000' }} />
                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#000000' }}>
                    Poule&Poulette
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  © 2025 Poule&Poulette. Alle rechten voorbehouden.
                </Typography>
                <Box sx={{ display: 'flex', gap: 3 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ cursor: 'pointer', '&:hover': { color: '#000000' } }}>
                    Privacy
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ cursor: 'pointer', '&:hover': { color: '#000000' } }}>
                    Voorwaarden
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ cursor: 'pointer', '&:hover': { color: '#000000' } }}>
                    Contact
                  </Typography>
                </Box>
              </Box>
            </Container>
          </Box>
        </>
      )}

      {/* Location Details Dialog */}
      <Dialog 
        open={locationDialogOpen} 
        onClose={() => {
          setLocationDialogOpen(false);
          // Reset map to overview when dialog is closed
          setTimeout(() => {
            setMapCenter([50.8503, 4.3517]);
            setMapZoom(7);
          }, 300);
        }}
        maxWidth="sm"
        fullWidth
      >
        {clickedLocation && (
          <>
            <DialogTitle sx={{ fontWeight: 'bold' }}>
              {clickedLocation.name}
              {(clickedLocation as any).distance && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Dichtstbijzijnde locatie ({(clickedLocation as any).distance} km van jouw locatie)
                </Typography>
              )}
            </DialogTitle>
            <DialogContent>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
                  {clickedLocation.address}, {clickedLocation.postcode} {clickedLocation.city}
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 2,
                  backgroundColor: clickedLocation.isOpen ? '#e8f5e8' : '#ffeaea',
                  borderRadius: 1,
                  px: 1.5,
                  py: 1,
                  border: `2px solid ${clickedLocation.isOpen ? '#4caf50' : '#f44336'}`
                }}>
                  <TimeIcon sx={{ 
                    fontSize: 20, 
                    mr: 1, 
                    color: clickedLocation.isOpen ? '#2e7d32' : '#d32f2f'
                  }} />
                  <Typography variant="body1" sx={{ 
                    fontWeight: 'bold',
                    color: clickedLocation.isOpen ? '#2e7d32' : '#d32f2f'
                  }}>
                    {clickedLocation.isOpen ? `Open tot ${clickedLocation.closingTime}` : 'Gesloten'}
                  </Typography>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  <PhoneIcon sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
                  {clickedLocation.phone}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                  {clickedLocation.services.map((service) => (
                    <Chip
                      key={service}
                      label={service}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.8rem' }}
                    />
                  ))}
                </Box>
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2, gap: 1, flexDirection: 'column', alignItems: 'stretch' }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => {
                  handleReservation(clickedLocation);
                  setLocationDialogOpen(false);
                }}
                sx={{ 
                  backgroundColor: '#4caf50',
                  color: '#ffffff',
                  fontWeight: 'bold',
                  py: 1.5,
                  fontSize: '1.1rem',
                  '&:hover': { backgroundColor: '#45a049' }
                }}
              >
                {t.reserveHere}
              </Button>
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<TakeawayIcon />}
                  onClick={() => {
                    handleTakeaway(clickedLocation);
                    setLocationDialogOpen(false);
                  }}
                  sx={{ flex: 1 }}
                >
                  {t.takeaway}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<DirectionsIcon />}
                  onClick={() => {
                    window.open(`https://maps.google.com/?q=${clickedLocation.address}, ${clickedLocation.city}`, '_blank');
                  }}
                  sx={{ flex: 1 }}
                >
                  {t.route}
                </Button>
              </Box>
              <Button
                onClick={() => setLocationDialogOpen(false)}
                sx={{ mt: 1 }}
              >
                {t.close}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Time Slot Selector Dialog */}
      <Dialog 
        open={timeSlotDialogOpen} 
        onClose={() => setTimeSlotDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxWidth: 700,
            width: '100%'
          }
        }}
      >
        {reservationLocation && (
          <>
            <DialogTitle sx={{ 
              fontWeight: 'bold', 
              textAlign: 'center',
              pb: 1,
              '& .MuiTypography-root': {
                fontSize: '1rem',
                color: '#000000'
              }
            }}>
              Kies een tijdsslot - {reservationLocation.name}
            </DialogTitle>
            <DialogContent sx={{ px: 3, pb: 3 }}>
              {/* Date Selector */}
              <Box sx={{ 
                mb: 3, 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center',
                textAlign: 'center'
              }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Selecteer een datum
                </Typography>
                <TextField
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ 
                    maxWidth: 200,
                    '& .MuiInputBase-input': {
                      textAlign: 'center'
                    }
                  }}
                />
              </Box>

              {/* Time Slots Grid */}
              {selectedDate && (
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                    Beschikbare tijdsslots
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 3, color: '#666' }}>
                    Elke reservering duurt 1 uur en 15 minuten
                  </Typography>
                  
                  <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' }, 
                    gap: 2 
                  }}>
                    {generateTimeSlots(selectedDate).map((slot) => (
                      <Button
                        key={slot.time}
                        variant={slot.isOccupied ? "outlined" : "contained"}
                        onClick={() => !slot.isOccupied && handleTimeSlotSelect(slot.time)}
                        disabled={slot.isOccupied}
                        sx={{
                          py: 2,
                          px: 2,
                          fontSize: '0.875rem',
                          fontWeight: 'bold',
                          backgroundColor: slot.isOccupied ? '#f5f5f5' : '#000000',
                          color: slot.isOccupied ? '#999' : '#ffffff',
                          border: slot.isOccupied ? '1px solid #ddd' : 'none',
                          '&:hover': {
                            backgroundColor: slot.isOccupied ? '#f5f5f5' : '#333333'
                          },
                          '&:disabled': {
                            backgroundColor: '#f5f5f5',
                            color: '#999'
                          }
                        }}
                      >
                        {slot.display}
                        {slot.isOccupied && (
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontSize: '0.7rem' }}>
                            Bezet
                          </Typography>
                        )}
                      </Button>
                    ))}
                  </Box>
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 0 }}>
              <Button
                onClick={() => setTimeSlotDialogOpen(false)}
                sx={{ color: '#666' }}
              >
                Annuleren
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Reservation Form Dialog */}
      <Dialog 
        open={reservationDialogOpen} 
        onClose={() => setReservationDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxWidth: 600,
            width: '100%'
          }
        }}
      >
        {reservationLocation && (
          <>
            <DialogTitle sx={{ 
              fontWeight: 'bold', 
              textAlign: 'center',
              pb: 1,
              '& .MuiTypography-root': {
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                color: '#666'
              }
            }}>
              RESERVERING
            </DialogTitle>
            <DialogContent sx={{ px: 3, pb: 3 }}>
              {/* Reservation Summary */}
              <Box sx={{ mb: 3, textAlign: 'center' }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Reservering voor {reservationForm.guests} personen, vandaag om {reservationForm.time || '18:00'} uur.
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Je reservering eindigt om:
                </Typography>
                <Box sx={{ 
                  backgroundColor: '#2c2c2c', 
                  color: '#ffffff', 
                  py: 1, 
                  px: 2, 
                  borderRadius: 1,
                  display: 'inline-block',
                  fontWeight: 'bold'
                }}>
                  {reservationForm.time ? 
                    new Date(`2000-01-01T${reservationForm.time}`).getHours() + 1 + ':15 uur' : 
                    '19:15 uur'
                  }
                </Box>
              </Box>

              {/* Customer Details Section */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                  Je gegevens
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: '#666' }}>
                  Je ontvangt een bevestiging van je reservering per e-mail.
                </Typography>

                {/* Salutation Radio Buttons */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    {['Dhr.', 'Mevr.', 'Mx.', 'Familie', 'Bedrijf'].map((salutation) => (
                      <Box key={salutation} sx={{ display: 'flex', alignItems: 'center' }}>
                        <input
                          type="radio"
                          id={salutation}
                          name="salutation"
                          value={salutation}
                          style={{ marginRight: '4px' }}
                        />
                        <label htmlFor={salutation} style={{ fontSize: '0.875rem' }}>
                          {salutation}
                        </label>
                      </Box>
                    ))}
                  </Box>
                </Box>

                {/* Name and Contact Fields */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                  <TextField
                    label="Voornaam"
                    value={reservationForm.firstName || ''}
                    onChange={(e) => setReservationForm({...reservationForm, firstName: e.target.value})}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="Achternaam"
                    value={reservationForm.lastName || ''}
                    onChange={(e) => setReservationForm({...reservationForm, lastName: e.target.value})}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="E-mail"
                    type="email"
                    value={reservationForm.email}
                    onChange={(e) => setReservationForm({...reservationForm, email: e.target.value})}
                    fullWidth
                    size="small"
                  />
                  <TextField
                    label="Telefoonnummer"
                    value={reservationForm.phone}
                    onChange={(e) => setReservationForm({...reservationForm, phone: e.target.value})}
                    fullWidth
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                          <Box sx={{ 
                            width: 16, 
                            height: 12, 
                            background: 'linear-gradient(to right, #000 33%, #ffd700 33% 66%, #ff0000 66%)',
                            borderRadius: '1px',
                            mr: 0.5
                          }} />
                          <Typography variant="caption">+32</Typography>
                        </Box>
                      ),
                    }}
                  />
                </Box>

                {/* Notes Field */}
                <TextField
                  label="Heb je belangrijke dieetwensen, allergieën of andere opmerkingen?"
                  multiline
                  rows={3}
                  value={reservationForm.notes}
                  onChange={(e) => setReservationForm({...reservationForm, notes: e.target.value})}
                  fullWidth
                  size="small"
                />
              </Box>

              {/* Marketing Opt-in */}
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <input
                    type="checkbox"
                    id="marketing"
                    defaultChecked
                    style={{ marginRight: '8px' }}
                  />
                  <label htmlFor="marketing" style={{ fontSize: '0.875rem' }}>
                    Mis niks van de nieuwste events, promoties en arrangementen van P&P Gent
                  </label>
                </Box>
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 0 }}>
              <Button
                variant="contained"
                onClick={handleSubmitReservation}
                disabled={!reservationForm.firstName || !reservationForm.lastName || !reservationForm.email || !reservationForm.phone}
                fullWidth
                sx={{ 
                  backgroundColor: '#8B4513',
                  color: '#ffffff',
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  '&:hover': { backgroundColor: '#A0522D' },
                  '&:disabled': { backgroundColor: '#ccc' }
                }}
              >
                Reserveer nu
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Box sx={{
          backgroundColor: snackbar.severity === 'success' ? '#4caf50' : '#f44336',
          color: '#ffffff',
          px: 3,
          py: 2,
          borderRadius: 1,
          fontWeight: 'bold'
        }}>
          {snackbar.message}
        </Box>
      </Snackbar>
    </Box>
  );
};

export default GuestReservationApp;
